import { Check, X } from "lucide-react";
import type { ServiceCategory } from "../api/CrmApi";
import { TONE_SOFT } from "shared/tones";

/** `value` plus every phrase in `text` (split on commas, semicolons, "|" and newlines), trimmed and de-duplicated
 *  case-insensitively. New phrases are typed by a human, so they are "research". */
export function withPhrases(value: ServiceCategory[], text: string): ServiceCategory[] {
  const next = [...value];
  for (const token of text.split(/[,;|\n]+/)) {
    const phrase = token.replace(/\s+/g, " ").trim();
    if (phrase && !next.some((p) => p.text.toLowerCase() === phrase.toLowerCase())) next.push({ text: phrase, source: "research" });
  }
  return next;
}

interface Props {
  value: ServiceCategory[];
  onChange: (items: ServiceCategory[]) => void;
  /** What's typed but not yet a chip; controlled by the parent so Save can include it (see withPhrases). */
  draft: string;
  onDraftChange: (draft: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Chip list of service phrases: type or paste a comma list, press Enter / comma (or leave the box) to add, click x
 *  to remove. Sheet-derived phrases are shown muted with a check button that confirms them as researched. */
export default function PhraseChipsInput({ value, onChange, draft, onDraftChange, disabled, placeholder }: Props) {
  function commit() {
    if (!draft.trim()) return;
    onChange(withPhrases(value, draft));
    onDraftChange("");
  }

  return (
    <div className="w-full bg-surface border border-border rounded-lg px-2 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:border-primary focus-within:ring-1 focus-within:ring-ring transition-colors">
      {value.map((item) => (
        <span
          key={item.text}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${item.source === "sheet" ? TONE_SOFT.neutral : TONE_SOFT.brand}`}
          title={item.source === "sheet" ? "From the CollabP sheet — unconfirmed. Confirm it if it is right." : undefined}
        >
          {item.text}
          {item.source === "sheet" && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(value.map((v) => (v.text === item.text ? { ...v, source: "research" } : v)))}
              className="opacity-70 hover:opacity-100 hover:text-success disabled:opacity-40"
              aria-label={`Confirm ${item.text}`}
            >
              <Check className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(value.filter((v) => v.text !== item.text))}
            className="opacity-70 hover:opacity-100 hover:text-destructive disabled:opacity-40"
            aria-label={`Remove ${item.text}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder ?? "e.g. transit data platforms, tolling systems" : ""}
        className="flex-1 min-w-[160px] bg-transparent text-sm text-foreground placeholder-muted-foreground px-1 py-1 focus:outline-none"
      />
    </div>
  );
}
